import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, FileDown, Loader2, PenLine, ShieldCheck, Stamp, X } from "lucide-react";
import { SignaturePad } from "../components/SignaturePad";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { contractHTML, contractPDFBlob } from "../lib/contractPrint";
import { saveBlob } from "../lib/download";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public/contracts`;

export default function ContractSign() {
  const { token } = useParams();
  const [c, setC] = useState(null);
  const [err, setErr] = useState("");
  const [name, setName] = useState("");
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState("");
  const sig = useRef(null);
  const [stamp, setStamp] = useState(null);
  const pickStamp = (f) => {
    if (!f) return;
    if (!/^image\/(png|jpeg)$/.test(f.type)) return toast.error("ارفع صورة PNG أو JPG");
    const img = new Image();
    img.onload = () => {
      const k = Math.min(1, 500 / Math.max(img.width, img.height));
      const cv = document.createElement("canvas");
      cv.width = Math.round(img.width * k); cv.height = Math.round(img.height * k);
      cv.getContext("2d").drawImage(img, 0, 0, cv.width, cv.height);
      setStamp(cv.toDataURL(f.type === "image/jpeg" ? "image/jpeg" : "image/png", 0.9));
      URL.revokeObjectURL(img.src);
    };
    img.src = URL.createObjectURL(f);
  };
  const urlFor = (id) => `${API}/${token}/file/${id}`;

  useEffect(() => {
    axios.get(`${API}/${token}`).then((r) => { setC(r.data); setName(r.data.party_name || ""); })
      .catch((e) => setErr(e?.response?.data?.detail || "الرابط غير صالح"));
  }, [token]);

  const sign = async () => {
    if (!agree) return toast.error("أكّد اطلاعك وموافقتك على بنود العقد");
    if (name.trim().length < 3) return toast.error("اكتب اسمك الكامل");
    if (sig.current?.isEmpty()) return toast.error("وقّع في المربع أولاً");
    setBusy("sign");
    try {
      const r = await axios.post(`${API}/${token}/sign`, { name: name.trim(), signature: sig.current.getSignature(), stamp });
      setC({ ...c, status: "signed", signed: r.data.signed });
      toast.success("تم توقيع العقد بنجاح");
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر التوقيع"); } finally { setBusy(""); }
  };
  const pdf = async () => {
    setBusy("pdf");
    try { await saveBlob(await contractPDFBlob(c, c.company, urlFor), `${c.number}.pdf`, "application/pdf"); }
    catch { toast.error("تعذّر إنشاء PDF"); } finally { setBusy(""); }
  };

  if (err) return <div dir="rtl" className="min-h-screen grid place-items-center bg-slate-50 p-6 text-center text-slate-600" data-testid="contract-sign-error">{err}</div>;
  if (!c) return <div className="min-h-screen grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  const signed = c.status === "signed";
  return (
    <div dir="rtl" className="min-h-screen bg-slate-100 py-4 sm:py-8 px-2" data-testid="contract-sign-page">
      <div className="mx-auto max-w-3xl space-y-3">
        <div className="flex items-center gap-2 rounded-xl bg-white border px-3 py-2 text-xs text-slate-600">
          <ShieldCheck className="h-4 w-4 text-emerald-600 shrink-0" /> رابط توقيع آمن ومخصص لك — {c.company?.name}
        </div>
        <iframe title="contract" srcDoc={contractHTML(c, c.company, urlFor)} className="w-full h-[70vh] rounded-xl border bg-white shadow-sm" data-testid="contract-sign-doc" />
        {signed ? (
          <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-4 space-y-3" data-testid="contract-signed-box">
            <p className="font-bold text-emerald-800 flex items-center gap-2"><CheckCircle2 className="h-5 w-5" /> تم توقيع العقد من {c.signed?.name}</p>
            <Button onClick={pdf} disabled={busy === "pdf"} className="gap-2 bg-emerald-600 hover:bg-emerald-700" data-testid="contract-sign-pdf">
              {busy === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} تحميل نسخة PDF موقّعة
            </Button>
          </div>
        ) : (
          <div className="rounded-2xl bg-white border p-4 space-y-3" data-testid="contract-sign-box">
            <p className="font-bold text-slate-800 flex items-center gap-2"><PenLine className="h-5 w-5 text-blue-600" /> التوقيع الإلكتروني</p>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="الاسم الكامل" data-testid="contract-sign-name" />
            <SignaturePad ref={sig} height={170} />
            <div className="rounded-xl border border-dashed p-3" data-testid="contract-stamp-box">
              <p className="text-sm font-semibold text-slate-700 flex items-center gap-1.5"><Stamp className="h-4 w-4 text-indigo-600" /> ختم الشركة <span className="text-xs font-normal text-slate-400">(اختياري)</span></p>
              {stamp ? (
                <div className="mt-2 flex items-center gap-3">
                  <img src={stamp} alt="stamp" className="h-20 max-w-[160px] object-contain rounded border bg-white" data-testid="contract-stamp-preview" />
                  <Button type="button" variant="outline" size="sm" className="gap-1" onClick={() => setStamp(null)} data-testid="contract-stamp-remove"><X className="h-3.5 w-3.5" /> إزالة</Button>
                </div>
              ) : (
                <label className="mt-2 inline-flex cursor-pointer items-center gap-2 rounded-lg bg-indigo-50 px-3 py-2 text-sm text-indigo-700 hover:bg-indigo-100 transition-colors">
                  <Stamp className="h-4 w-4" /> إرفاق صورة الختم (PNG / JPG)
                  <input type="file" accept="image/png,image/jpeg" className="hidden" data-testid="contract-stamp-input" onChange={(e) => { pickStamp(e.target.files?.[0]); e.target.value = ""; }} />
                </label>
              )}
            </div>
            <label className="flex items-start gap-2 text-sm text-slate-700">
              <input type="checkbox" className="mt-1" checked={agree} onChange={(e) => setAgree(e.target.checked)} data-testid="contract-sign-agree" />
              اطلعت على جميع بنود العقد وأوافق عليها، وأقرّ بأن توقيعي الإلكتروني ملزم قانوناً.
            </label>
            <Button onClick={sign} disabled={busy === "sign"} className="w-full gap-2 bg-blue-600 hover:bg-blue-700 h-11" data-testid="contract-sign-submit">
              {busy === "sign" && <Loader2 className="h-4 w-4 animate-spin" />} توقيع العقد
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
